import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, PlusCircle } from 'lucide-react';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface CourseData {
  id: string;
  title: string;
  description: string | null;
  status: 'published' | 'draft';
  lesson_count: number;
  created_at_formatted: string;
  updated_at_formatted: string;
}

interface CoursesListProps {
  courses: CourseData[];
  isLoading: boolean;
  formatDate: (date: string) => string;
}

const CoursesList: React.FC<CoursesListProps> = ({ courses, isLoading, formatDate }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <p>Loading courses...</p>
      </div>
    );
  }

  const publishedCourses = courses.filter(course => course.status === 'published');
  const draftCourses = courses.filter(course => course.status === 'draft');

  return (
    <Tabs defaultValue="all" className="mt-6">
      <TabsList>
        <TabsTrigger value="all">All Courses ({courses.length})</TabsTrigger>
        <TabsTrigger value="published">Published ({publishedCourses.length})</TabsTrigger>
        <TabsTrigger value="drafts">Drafts ({draftCourses.length})</TabsTrigger>
      </TabsList>
      
      <TabsContent value="all" className="mt-6">
        {renderCoursesList(courses, formatDate)}
      </TabsContent>
      
      <TabsContent value="published" className="mt-6">
        {renderCoursesList(publishedCourses, formatDate)}
      </TabsContent>
      
      <TabsContent value="drafts" className="mt-6">
        {renderCoursesList(draftCourses, formatDate)}
      </TabsContent>
    </Tabs>
  );
};

const renderCoursesList = (courses: CourseData[], formatDateFn: (date: string) => string) => {
  if (courses.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-medium">No courses found</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Start by creating your first course.
        </p>
        <Button asChild className="mt-4">
          <Link to="/admin/course/new">
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Course
          </Link>
        </Button>
      </div>
    );
  }
  
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {courses.map(course => (
        <Card key={course.id} className="flex flex-col h-auto overflow-hidden">
          <CardHeader className="pb-2 space-y-0">
            <div className="flex items-center justify-between mb-1">
              <Badge variant={course.status === 'published' ? "default" : "secondary"} className="text-xs px-2 py-0">
                {course.status === 'published' ? "Published" : "Draft"}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {course.lesson_count} {course.lesson_count === 1 ? 'lesson' : 'lessons'}
              </span>
            </div>
            <CardTitle className="line-clamp-1 text-base">{course.title}</CardTitle>
            <CardDescription className="line-clamp-1 text-xs mt-1">{course.description}</CardDescription>
          </CardHeader>
          <CardContent className="pb-2 pt-0">
            <div className="flex flex-col space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created:</span>
                <span>{formatDateFn(course.created_at_formatted)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Updated:</span>
                <span>{formatDateFn(course.updated_at_formatted)}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-2">
            <Button asChild size="sm" className="w-full">
              <Link to={`/admin/course/${course.id}`}>
                Edit Course
              </Link>
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default CoursesList;
